import type {ReactNode} from 'react';
import React, {useState} from 'react';
import {Image} from 'react-bootstrap';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import styles from './generated-image.module.scss';
import type {GeneratedImageProps} from '@Components/ai-chat-window/components/generated-image/generated-image.types';
import {
  MAX_CHAT_IMAGE_DIMENSION,
  MAX_CHAT_IMAGE_HEIGHT_SMALL_SCREEN,
  MAX_CHAT_IMAGE_WIDTH_SMALL_SCREEN,
} from '@Components/ai-chat-window/components/generated-image/generated-image.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {getScaledDimensionsForImage} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {GeneratedImageActionsIcons} from '@Components/ai-chat-window/components/generated-image-actions-icons/generated-image-actions-icons';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {SwipeableReattachWrapper} from '@Components/ai-chat-window/components/swipeable-reattach-wrapper/swipeable-reattach-wrapper';

export function GeneratedImage({
  className = '',
  skeltonLoaderTheme = SkeletonBrickTheme.LIGHT_TWO,
  showFirstImageGenerationDelight = false,
  ...props
}: GeneratedImageProps): ReactNode {
  const {width, height} = props;

  const [loaded, setLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = useState(!showFirstImageGenerationDelight);

  const handleAnimationEnd = (): void => {
    setIsAnimationComplete(true);
  };

  const handleImageLoad = (): void => {
    setLoaded(true);
  };

  const limitWidth = props.isMobile ? MAX_CHAT_IMAGE_WIDTH_SMALL_SCREEN : MAX_CHAT_IMAGE_DIMENSION;
  const limitHeight = props.isMobile ? MAX_CHAT_IMAGE_HEIGHT_SMALL_SCREEN : MAX_CHAT_IMAGE_DIMENSION;
  const {width: displayWidth, height: displayHeight} = getScaledDimensionsForImage(width, height, limitWidth, limitHeight);

  const handleImageClick = (): void => {
    if (!loaded) {
      return;
    }

    props.onImageClick?.();
  };

  const handleContextMenu = (e: React.MouseEvent): void => {
    e.preventDefault();
  };

  const isImageReady = (): boolean => {
    return loaded && isAnimationComplete && !props.isWaitingForImageGeneratedEvent;
  };

  const getImageHoverOverlay = (): ReactNode => {
    if (!isImageReady()) {
      return null;
    }

    return <div className={styles.imageHoverOverlay} />;
  };

  const getReattachOption = (): ReactNode => {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={<Tooltip className="spacing-m-b-1 _rounded-arrow">{window.i18next.t('pmwjs_reattach')}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
      >
        <div>
          <Icon
            className="content-body-white"
            icon="icon-paperclip"
            type={IconType.DARK_GHOST}
            size={IconSize.SIZE_ICON_16}
            shape={IconShape.CIRCLE}
            onClick={(e) => {
              e.stopPropagation();
              props.onReattach?.();
            }}
            ariaLabel={window.i18next.t('pmwjs_reattach')}
          />
        </div>
      </OverlayTrigger>
    )
  }

  const getOverlayActionIcons = (): ReactNode => {
    if (!isImageReady()) {
      return null;
    }

    return (
      <div className={styles.actionIconsWrapper}>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          overlay={<Tooltip className="spacing-m-b-1 _rounded-arrow">{window.i18next.t('pmwjs_edit_in_editor')}</Tooltip>}
          delay={{show: 0, hide: 0}}
          placement="top"
        >
          <div>
            <Icon
              className="content-body-white"
              icon="icon-pencil"
              type={IconType.DARK_GHOST}
              size={IconSize.SIZE_ICON_16}
              shape={IconShape.CIRCLE}
              onClick={(e) => {
                e.stopPropagation();
                props.generatedImagePartActionsProps.onOpenPosterInEditor();
              }}
              ariaLabel={window.i18next.t('pmwjs_edit_in_editor')}
            />
          </div>
        </OverlayTrigger>
        {getReattachOption()}
        <OverlayTrigger
          trigger={['hover', 'focus']}
          overlay={<Tooltip className="spacing-m-b-1 _rounded-arrow">{window.i18next.t('pmwjs_view_image')}</Tooltip>}
          delay={{show: 0, hide: 0}}
          placement="top"
        >
          <div>
            <Icon
              className="content-body-white"
              icon="icon-expand"
              type={IconType.DARK_GHOST}
              size={IconSize.SIZE_ICON_16}
              shape={IconShape.CIRCLE}
              onClick={(e) => {
                e.stopPropagation();
                props.onImageClick?.();
              }}
              ariaLabel={window.i18next.t('pmwjs_view_image')}
            />
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  const getImageClassNames = (): string => {
    return `
      _full-width
      _full-height
      ${styles.image}
      ${props.isMobile ? styles.smallImage : ''}
      ${className}
      ${loaded ? styles.visible : styles.hidden}
      ${loaded && showFirstImageGenerationDelight ? styles.clickable : ''}
      ${loaded && showFirstImageGenerationDelight ? styles.imageAnimation : ''}
      ${props.isWaitingForImageGeneratedEvent ? styles.blurred : ''}
      `;
  };

  const renderImage = (): ReactNode => {
    if (props.isSkeletonOnly) {
      return <SkeletonBrick width={displayWidth} height={displayHeight} theme={skeltonLoaderTheme} className={`${styles.skeleton} ${className}`} />;
    }

    return (
      <>
        {!loaded ? <SkeletonBrick width={displayWidth} height={displayHeight} theme={skeltonLoaderTheme} className={`${styles.skeleton} ${className}`} /> : null}
        <Image
          onAnimationEnd={handleAnimationEnd}
          fluid
          loading={'lazy'}
          src={props.src}
          onLoad={handleImageLoad}
          onContextMenu={handleContextMenu}
          className={getImageClassNames()}
        />
      </>
    );
  };

  const renderFirstImageGenerationDelight = (): ReactNode => {
    if (loaded && showFirstImageGenerationDelight) {
      return <div className={`_full-width _full-height _position-absolute ${styles.imageBackgroundGlow} ${loaded ? styles.glowAnimation : ''}`} />;
    }

    return null;
  };

  const renderImageActionIcons = (): ReactNode => {
    if (isImageReady()) {
      return (
        <GeneratedImageActionsIcons
          isMobile={props.isMobile}
          isLastMessage={props.isLastMessage}
          hashedId={props.hashedId}
          chatId={props.chatId}
          isVisible={isHovered}
          message={props.message}
          messages={props.messages}
          isMessageStreaming={props.isMessageStreaming}
          hasLayeredPoster={props.hasLayeredPoster}
          {...props.generatedImagePartActionsProps}
        />
      );
    }

    return null;
  };

  return (
    <div
      className={`flex-h-row gap-2 ${props.isMobile ? '' : 'align-items-start'}`}
      onPointerEnter={() => {
        setIsHovered(true);
      }}
      onPointerLeave={() => {
        setIsHovered(false);
      }}
    >
      <SwipeableReattachWrapper onReattach={props.onReattach} enabled={!!props.isMobile}>
        <div
          onClick={handleImageClick}
          onContextMenu={handleContextMenu}
          className={`_fit-width _position-relative ${styles.imageContainer} ${props.isMobile ? styles.smallImageContainer : ''}`}
        >
          {renderFirstImageGenerationDelight()}
          {renderImage()}
          {getImageHoverOverlay()}
          {getOverlayActionIcons()}
        </div>
      </SwipeableReattachWrapper>

      {renderImageActionIcons()}
    </div>
  );
}
